/* eslint-disable max-lines */
import { useCameras, useLocationById } from '@hakimo-ui/hakimo/data-access';
import { Camera } from '@hakimo-ui/hakimo/types';
import { LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import {
  Button,
  Checkbox,
  Checkboxes,
  Modal,
  InputField,
} from '@hakimo-ui/shared/ui-base';
import { CheckboxItem } from '@hakimo-ui/shared/ui-base';
import { useState, useEffect, useMemo } from 'react';

function getCameraSearchParams(locationName: string): string {
  const params = new URLSearchParams();
  params.append('locations', locationName);
  return params.toString();
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (selectedCameraNames: string[]) => void;
  locationId: string;
  initialSelectedCameras?: string[];
}

export function CameraSelectionModal(props: Props) {
  const {
    open,
    onClose,
    onSubmit,
    locationId,
    initialSelectedCameras = [],
  } = props;

  const [selectedCameras, setSelectedCameras] = useState<
    CheckboxItem<string>[]
  >([]);
  const [searchValue, setSearchValue] = useState('');
  const { data: locationData, isLoading: isLocationLoading } =
    useLocationById(locationId);
  const searchParams = locationData?.name
    ? getCameraSearchParams(locationData.name)
    : '';
  const { data: camerasData, isLoading: isCamerasLoading } = useCameras(
    searchParams,
    open && !!locationData?.name
  );

  const cameras = camerasData?.items || [];
  const isLoading = isLocationLoading || isCamerasLoading;

  const filteredCameras = useMemo(() => {
    if (!searchValue.trim()) {
      return cameras;
    }
    return cameras.filter((camera) =>
      camera.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [cameras, searchValue]);

  const cameraCheckboxItems: CheckboxItem<string>[] = filteredCameras.map(
    (camera: Camera) => ({
      id: camera.name,
      label: camera.name,
      value: camera.name,
    })
  );

  const allFilteredSelected =
    filteredCameras.length > 0 &&
    filteredCameras.every((camera) =>
      selectedCameras.some((item) => item.id === camera.name)
    );

  useEffect(() => {
    if (open && cameras.length > 0) {
      const allCameraItems = cameras.map((camera: Camera) => ({
        id: camera.name,
        label: camera.name,
        value: camera.name,
      }));
      const initialSelected = allCameraItems.filter((item) =>
        initialSelectedCameras.includes(item.id)
      );
      setSelectedCameras(initialSelected);
    }
  }, [open, cameras.length, initialSelectedCameras]);

  const handleSubmit = () => {
    const selectedCameraNames = selectedCameras.map((item) => item.value);
    onSubmit(selectedCameraNames);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSelectAll = (checked: boolean) => {
    const filteredIds = new Set(filteredCameras.map((camera) => camera.name));
    if (checked) {
      const toAdd = cameraCheckboxItems.filter(
        (item) => !selectedCameras.some((s) => s.id === item.id)
      );
      setSelectedCameras([...selectedCameras, ...toAdd]);
    } else {
      setSelectedCameras(
        selectedCameras.filter((item) => !filteredIds.has(item.id))
      );
    }
  };

  const actions = (
    <>
      <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
        Submit
      </Button>
      <Button onClick={handleCancel}>Cancel</Button>
    </>
  );

  return (
    <Modal
      title="Associate cameras with note"
      open={open}
      onClose={onClose}
      footer={actions}
    >
      <div className="relative mx-8 my-4 h-[30rem] min-w-96 overflow-auto px-1 transition-all duration-300 ease-in-out">
        <div className="sticky top-0 my-2 flex">
          <InputField
            label=""
            onChange={onSearchChange}
            value={searchValue}
            type="search"
            placeholder="Search with camera name..."
          />
        </div>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <LoadingIndicator text="Loading cameras..." />
          </div>
        ) : cameras.length === 0 ? (
          <div className="py-4 text-center">
            No cameras available for this location.
          </div>
        ) : filteredCameras.length === 0 ? (
          <div className="py-4 text-center">
            No cameras found matching your search.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="border-onlight-line-2 dark:border-ondark-line-2 border-b pb-2">
              <Checkbox
                label="Select All"
                checked={allFilteredSelected}
                onChange={handleSelectAll}
              />
            </div>
            <Checkboxes
              items={cameraCheckboxItems}
              value={selectedCameras}
              onChange={setSelectedCameras}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default CameraSelectionModal;
